/**
 * The Steps tab's graph, as plain functions of the team document: steps become nodes and
 * hand-offs edges, laid out top to bottom on every change. What starts a step (its triggers)
 * is a node of its own above it. Positions are never saved.
 */
import { MarkerType, type Edge as FlowEdge, type Node } from 'reactflow';
import { autoLayout } from '@/services/diagram.service';
import { connectorOf, emptyMember, eventLabel, fromList, triggerLabel, triggerSource, type Connection, type Edge, type Member, type TeamDoc } from './catalog';

export const NODE_WIDTH = 240;
export const NODE_HEIGHT = 76;

/** A step's connectors, each with how many of its tools are on it, in the order it lists them. */
export type ConnectorCount = { connector: string; count: number };
export type StepNodeData = { name: string; model: string; budget: number | null; connectors: ConnectorCount[] };
/** What starts a step: `connector` is the trigger's connector, or `user` / `schedule`. */
export type StartsNodeData = { triggers: { label: string; connector: string }[] };

// Nodes are keyed by position, not name, so a step being renamed stays selected.
export const stepNodeId = (i: number) => `step-${i}`;
export const stepIndex = (nodeId: string) => Number(nodeId.slice('step-'.length));
export const handoffIndex = (edgeId: string) => Number(edgeId.slice('handoff-'.length));
export const startsNodeId = (i: number) => `starts-${i}`;

export const conditionLabel = (e: Edge) => {
  const clauses = Object.entries(e.when).map(([k, v]) => `${k} = ${fromList(v)}`);
  return clauses.length ? clauses.join(' and ') : 'any output';
};

export function stepsGraph(doc: TeamDoc, connections: Connection[] = []): { nodes: Node<StepNodeData | StartsNodeData>[]; edges: FlowEdge[] } {
  const ids = new Map(doc.members.map((m, i) => [m.name, stepNodeId(i)] as const));
  const nodes: Node<StepNodeData | StartsNodeData>[] = doc.members.flatMap((m, i) => {
    const counts = new Map<string, number>();
    for (const c of m.capabilities) {
      const connector = connectorOf(c.connection, connections);
      if (connector) counts.set(connector, (counts.get(connector) ?? 0) + 1);
    }
    const step: Node<StepNodeData> = {
      id: stepNodeId(i),
      type: 'step',
      position: { x: 0, y: 0 },
      data: { name: m.name, model: m.model ?? 'sonnet', budget: m.budget_usd, connectors: [...counts].map(([connector, count]) => ({ connector, count })) },
    };
    if (!m.triggers.length) return [step];
    const triggers = m.triggers.map((t) => {
      const connector = triggerSource(t, connections);
      return { connector, label: triggerLabel(t, eventLabel(connector, t.event)) };
    });
    return [step, { id: startsNodeId(i), type: 'starts', position: { x: 0, y: 0 }, data: { triggers } }];
  });
  const starts: FlowEdge[] = doc.members.flatMap((m, i) =>
    m.triggers.length ? [{ id: `starts-edge-${i}`, source: startsNodeId(i), target: stepNodeId(i), type: 'straight', data: { starts: true } }] : [],
  );
  const handoffs: FlowEdge[] = doc.edges.flatMap((e, i) => {
    const source = ids.get(e.from);
    const target = ids.get(e.to);
    // A hand-off naming a step that isn't there yet (mid-edit) has nothing to draw between.
    if (!source || !target) return [];
    return [
      {
        id: `handoff-${i}`,
        source,
        target,
        type: 'smoothstep',
        label: e.gate ? `${conditionLabel(e)} · ${e.gate.connection != null ? 'Slack approval' : 'approval'}` : conditionLabel(e),
        markerEnd: { type: MarkerType.ArrowClosed },
        data: { gated: !!e.gate },
      },
    ];
  });
  const edges = [...starts, ...handoffs];
  const positions = autoLayout(nodes, edges, 'LINEAR', NODE_WIDTH, NODE_HEIGHT, 80, 60, 'TB');
  // A hand-off back up the graph (or to itself) leaves and enters on the right, around the
  // steps, rather than over the hand-offs coming down between the same steps.
  const up = (e: FlowEdge) => (positions[e.target]?.y ?? 0) <= (positions[e.source]?.y ?? 0);
  return {
    nodes: nodes.map((n) => ({ ...n, position: positions[n.id] ?? n.position })),
    edges: edges.map((e) => (up(e) ? { ...e, sourceHandle: 'back-out', targetHandle: 'back-in' } : { ...e, sourceHandle: 'out', targetHandle: 'in' })),
  };
}

/** A new step, last; after a step, with a hand-off to it on any output and no approval. */
export function addStep(doc: TeamDoc, after?: string): TeamDoc {
  // Hand-offs name steps, so the new one must not reuse a name left by a removed or renamed step.
  let n = doc.members.length + 1;
  while (doc.members.some((m) => m.name === `step-${n}`)) n++;
  const m = emptyMember(n);
  return {
    ...doc,
    members: [...doc.members, m],
    edges: after === undefined ? doc.edges : [...doc.edges, { from: after, when: {}, gate: null, to: m.name }],
  };
}

/** Step i replaced; a renamed step keeps its hand-offs. */
export function setStep(doc: TeamDoc, i: number, m: Member): TeamDoc {
  const old = doc.members[i]!;
  const edges =
    old.name === m.name
      ? doc.edges
      : doc.edges.map((e) => ({ ...e, from: e.from === old.name ? m.name : e.from, to: e.to === old.name ? m.name : e.to }));
  return { ...doc, members: doc.members.map((x, j) => (j === i ? m : x)), edges };
}

/** Step i and its hand-offs gone; the last step can't be removed (null). */
export function removeStep(doc: TeamDoc, i: number): TeamDoc | null {
  if (doc.members.length <= 1) return null;
  const name = doc.members[i]!.name;
  return { ...doc, members: doc.members.filter((_, j) => j !== i), edges: doc.edges.filter((e) => e.from !== name && e.to !== name) };
}
